import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { assetUrl } from '../api/assets';
import { useT } from '../i18n';
import { OfflineBanner, SyncErrorBanner } from '../components/chat';
import { useCommandScope } from '../commands/registry';
import type { AppHeaderConfig } from '../components/layout/AppHeader';
import { AppNav, useNavShape } from '../components/layout/AppNav';
import { EmailVerifyBanner } from '../components/layout/EmailVerifyBanner';
import { PaneShell } from '../components/layout/PaneShell';
import {
  CoordinatedShellHeader,
  ROOT_SHELL_HEADER_SCOPE,
  ShellHeaderRegistryProvider,
  useActiveShellHeader,
  useHasRegisteredShellHeaders,
  useShellHeaderRegistry,
  useShellHeaderScope,
} from '../components/layout/ShellHeaderRegistry';
import { navItemsFor } from '../layout/navItems';
import { useNavFocus } from '../layout/useNavFocus';
import { useScrollPolicy } from './useScrollPolicy';
import styles from './AppShell.module.css';

// ============================================================================
// AppShell (E2/Paket D·E) — die Baushell als Layoutroute (docs/30 §4/§7).
// ----------------------------------------------------------------------------
// Loest den frueheren `<AppLayout chromeless>` ab: statt Flex-Shell + BottomNav
// jetzt PaneShell(Rail=AppNav | Buehne=Outlet). W1 reaktiviert den vorhandenen
// Header-Vertrag additiv: migrierte Seiten melden ihre Konfiguration, waehrend
// Alt-Seiten ihren eigenen AppHeader vorerst unveraendert behalten.
//
// Der Kontext behaelt die Signatur `setHeroBanner`/`resetHeroBanner`, damit die
// sechs Aufrufstellen (dashboard/characters/table-rounds/world-elements/
// milestones/admin) UNVERAENDERT bleiben (docs/30 §7.4). `setSceneBanner` ist
// als Alias schon angelegt (die Umbenennung Hero→SceneCard folgt in E7).
// setHeader/resetHeader sind owner- und pane-sicher; das Cleanup einer alten
// Route kann daher keinen neueren Header aus dem Register entfernen.
// ============================================================================

export interface SceneBannerConfig {
  landscape: string;
  portrait: string;
}
/** Alt-Name — bleibt exportiert, damit bestehende Importe gueltig sind. */
export type HeroBannerConfig = SceneBannerConfig;

export interface AppShellContextValue {
  setHeader: (header: AppHeaderConfig) => void;
  resetHeader: () => void;
  setHeroBanner: (heroBanner: SceneBannerConfig | null) => void;
  resetHeroBanner: () => void;
}
/** Alt-Name des Kontext-Typs (bestehende Importe + Tests). */
export type AppLayoutContextValue = AppShellContextValue;

export const AppShellContext = createContext<AppShellContextValue | null>(null);
/** Alt-Name des Kontexts (bestehende Importe). */
export const AppLayoutContext = AppShellContext;

export interface AppShellProps {
  children?: ReactNode;
  heroBanner?: SceneBannerConfig | null;
}

function cx(...classes: (string | false | null | undefined)[]): string {
  return classes.filter(Boolean).join(' ');
}

function readIsBetaEnvironment(): boolean {
  if (typeof document === 'undefined') return false;
  return document.documentElement.dataset.appEnv === 'beta';
}

export function useAppShell(): AppShellContextValue {
  const value = useContext(AppShellContext);
  const registry = useShellHeaderRegistry();
  const scope = useShellHeaderScope();
  const owner = useId();
  const registerHeader = registry?.setHeader;
  const unregisterHeader = registry?.resetHeader;
  const fallbackSetHeader = value?.setHeader;
  const fallbackResetHeader = value?.resetHeader;
  const setHeader = useCallback((header: AppHeaderConfig) => {
    if (registerHeader) registerHeader(scope, owner, header);
    else fallbackSetHeader?.(header);
  }, [fallbackSetHeader, owner, registerHeader, scope]);
  const resetHeader = useCallback(() => {
    if (unregisterHeader) unregisterHeader(scope, owner);
    else fallbackResetHeader?.();
  }, [fallbackResetHeader, owner, scope, unregisterHeader]);
  const scopedValue = useMemo(
    () => (value ? { ...value, setHeader, resetHeader } : null),
    [resetHeader, setHeader, value],
  );
  if (!scopedValue) throw new Error('useAppShell must be used within AppShell');
  return scopedValue;
}
/** Alt-Name des Hooks (die sechs Hero-Aufrufstellen). */
export const useAppLayout = useAppShell;

function AppShellContent({ children, heroBanner = null }: AppShellProps) {
  const { t } = useT();
  // Zentrale Scroll-Policy (F-01) — unveraendert, Fenster-Scroll (Paket K/E5a').
  useScrollPolicy();
  const [isBeta] = useState(readIsBetaEnvironment);
  const [runtimeHeroBanner, setRuntimeHeroBanner] = useState<SceneBannerConfig | null | undefined>(undefined);
  const activeHeroBanner = runtimeHeroBanner === undefined ? heroBanner : runtimeHeroBanner;
  const [heroBannerFailed, setHeroBannerFailed] = useState(false);
  const activeShellHeader = useActiveShellHeader();
  const hasRegisteredHeaders = useHasRegisteredShellHeaders();
  // Alt-Seiten mit eigenem Header behalten die bisherige Hero-Reserve. Fuer
  // migrierte Seiten folgt sie dagegen dem tatsaechlich koordinierten Header;
  // breite Split-Header liegen im Fluss und brauchen keine globale Reserve.
  const reserveFixedHeader = Boolean(activeShellHeader?.transparent)
    || (Boolean(activeHeroBanner) && !hasRegisteredHeaders);

  // ⌘0 / ⌘\ schalten die Rail durch (docs/30 §6). Ueber die Kommando-Registry
  // (E4a) statt eines eigenen Listeners — der EINE Dispatcher (HotkeyHost)
  // uebernimmt Normalisierung, IME-/Textfeld-Regel und e.repeat. Die KeySpecs
  // sind exakt das, was normalize() erzeugt: ⌘0 → 'mod+0', ⌘\ → 'mod+\'
  // (bei ⌘\ ist altKey false, also e.key.toLowerCase() = '\').
  const { toggle } = useNavShape();
  const navigate = useNavigate();
  // Paket L (docs/30 §9.2 / §5.5 Lauf-4-L): ⌘1…5 springen in die fuenf
  // primaeren Bereiche. EINE Nav-Wahrheit — die KeySpecs (mod+1…mod+5) und die
  // Ziele stammen direkt aus NAV_ITEMS (item.shortcut/item.to), damit sie nicht
  // von der Bar/Rail abdriften. adminOnly wird bewusst ausgelassen (kein festes
  // Kuerzel fuer eine bedingt sichtbare Route). Latest-Ref-Proxys von
  // useCommandScope halten die run-Closures ueber navigate frisch.
  //
  // Reserviert (KEIN lebendes Kommando): die KeySpec 'mod+alt+KeyI' ist fuer den
  // Inspector-/Schirm-Toggle (E7) vorgesehen. Der Inspector entsteht erst dort;
  // bis dahin gibt es kein Ziel, also registrieren wir hier bewusst KEINEN toten
  // Shortcut. Die e.code-Aufloesung fuer ⌥I ist bereits durch normalize()-Tests
  // in commands/useHotkeys abgedeckt.
  // BEWUSSTE EINSCHRÄNKUNG (P26-Verify dokumentiert): ⌘1…5 leben im
  // 'shell'-Scope — auf den Chat-Flächen (eigene Layoutwurzel bis P5) sind sie
  // damit inaktiv. Ab P5 ziehen die Chats in die Shell, dann gilt der Scope
  // automatisch dort; eine globale Registrierung vorher hieße Navigation aus
  // laufenden Streams heraus ohne Guard. overlayDepth-Gate: bei offener
  // modaler Palette/Hilfe navigiert NICHTS unter dem Backdrop.
  // W2-B/X-09: die Kuerzel folgen der Leiste dieses Kontos (fokussiert oder
  // bekannt) — sonst sprang ⌘4 bei Neukonten in einen ausgeblendeten Bereich.
  const navFocused = useNavFocus();
  const areaCommands = useMemo(
    () =>
      navItemsFor(navFocused).filter((item) => item.shortcut && item.primary && !item.adminOnly).map((item) => ({
        id: `nav.go.${item.id}`,
        titleKey: item.labelKey,
        keys: [item.shortcut!],
        group: 'navigation',
        when: (ctx: { overlayDepth: number }) => ctx.overlayDepth === 0,
        run: () => navigate(item.to),
      })),
    [navigate, navFocused],
  );
  useCommandScope('shell', [
    {
      id: 'nav.toggleRail',
      titleKey: 'nav.rail_toggle',
      keys: ['mod+0', 'mod+\\'],
      group: 'ansicht',
      noRepeat: true,
      when: (ctx) => ctx.overlayDepth === 0,
      run: () => toggle(),
    },
    ...areaCommands,
  ]);

  useEffect(() => {
    setHeroBannerFailed(false);
  }, [activeHeroBanner?.landscape, activeHeroBanner?.portrait]);

  const resetHeroBanner = useCallback(() => {
    setRuntimeHeroBanner(undefined);
  }, []);

  // --- L1: gemessene Hoehe der Shell-Kopfzeile -------------------------------
  // Die Kopfzeile liegt IM FLUSS ueber der Buehne. Flaechen, die sich selbst an
  // 100dvh binden (SplitView, docs/30 §8.3), muessen diese Zeile abziehen —
  // sonst steht unter jeder Split-Seite ein Leerscroll in Kopfzeilenhoehe
  // (gemessen: 62 px, u.a. auf /soundboard). Gemessen statt geraten, weil die
  // Hoehe an Schriftgroesse, Sprache und Safe-Area haengt.
  const shellRef = useRef<HTMLDivElement | null>(null);
  // Das <header>-Element kommt per Ref-Callback aus CoordinatedShellHeader —
  // frueher lief dieser Effekt OHNE Abhaengigkeitsliste und legte damit bei
  // JEDEM Render der Shell (jede Navigation, jedes setHeader einer Seite) einen
  // neuen ResizeObserver an, samt zweier querySelector-Laeufe ueber den ganzen
  // Teilbaum. Jetzt laeuft er nur, wenn die Kopfzeile wirklich entsteht,
  // verschwindet oder ihre Instanz wechselt.
  const [shellHeaderEl, setShellHeaderEl] = useState<HTMLElement | null>(null);
  const lastHeaderBlock = useRef<number | null>(null);
  useLayoutEffect(() => {
    const root = shellRef.current;
    if (!root) return undefined;
    const apply = () => {
      // Eine fixierte (transparente) Kopfzeile belegt keinen Fluss — dann 0.
      const flows = shellHeaderEl ? getComputedStyle(shellHeaderEl).position !== 'fixed' : false;
      const value = shellHeaderEl && flows
        ? Math.round(shellHeaderEl.getBoundingClientRect().height)
        : 0;
      // Nur schreiben, wenn sich der gerundete Wert geaendert hat: der
      // ResizeObserver feuert auch bei reinen BREITENaenderungen.
      if (lastHeaderBlock.current === value) return;
      lastHeaderBlock.current = value;
      root.style.setProperty('--lorath-shell-header-block', `${value}px`);
    };
    apply();
    if (!shellHeaderEl || typeof ResizeObserver !== 'function') return undefined;
    const observer = new ResizeObserver(apply);
    observer.observe(shellHeaderEl);
    return () => observer.disconnect();
  }, [shellHeaderEl]);

  const registry = useShellHeaderRegistry();
  const registerHeader = registry?.setHeader;
  const unregisterHeader = registry?.resetHeader;
  const setRootHeader = useCallback((header: AppHeaderConfig) => {
    registerHeader?.(ROOT_SHELL_HEADER_SCOPE, 'app-shell-context', header);
  }, [registerHeader]);
  const resetRootHeader = useCallback(() => {
    unregisterHeader?.(ROOT_SHELL_HEADER_SCOPE, 'app-shell-context');
  }, [unregisterHeader]);

  const contextValue = useMemo<AppShellContextValue>(() => ({
    setHeader: setRootHeader,
    resetHeader: resetRootHeader,
    setHeroBanner: setRuntimeHeroBanner,
    resetHeroBanner,
  }), [resetHeroBanner, resetRootHeader, setRootHeader]);

  const stage = (
    <>
      <CoordinatedShellHeader placement="shell" elementRef={setShellHeaderEl} />
      <main
        className={cx(styles.main, reserveFixedHeader && styles.mainUnderFixed)}
        data-shell-header-reserve={reserveFixedHeader ? 'true' : 'false'}
      >
        <EmailVerifyBanner />
        {children ?? <Outlet context={contextValue} />}
      </main>
    </>
  );

  const backdrop = activeHeroBanner && !heroBannerFailed ? (
    <picture className={styles.heroBanner} aria-hidden="true">
      <source media="(orientation: portrait)" srcSet={assetUrl(activeHeroBanner.portrait)} />
      <img
        src={assetUrl(activeHeroBanner.landscape)}
        alt=""
        className={styles.heroImage}
        onError={() => setHeroBannerFailed(true)}
      />
    </picture>
  ) : null;

  return (
    <AppShellContext.Provider value={contextValue}>
      <div ref={shellRef} className={cx(
        styles.shell,
        isBeta && styles.shellBeta,
        activeHeroBanner && styles.shellWithHero,
        activeHeroBanner && 'lorath-shell-with-hero',
      )}>
        {isBeta ? <div className={styles.betaBanner}>{t('coins_global.beta_banner')}</div> : null}
        <PaneShell variant="build" rail={<AppNav />} stage={stage} backdrop={backdrop} />
        {/* Die Mini-Leiste der Klangbuehne haengt bewusst NICHT hier, sondern
            in App.tsx innerhalb des Routers: die Vollbild-Chats liegen
            ausserhalb dieser Shell und brauchen sie genauso. */}
        <OfflineBanner className={styles.offlineBanner} />
        <SyncErrorBanner className={styles.offlineBanner} />
      </div>
    </AppShellContext.Provider>
  );
}

export function AppShell(props: AppShellProps) {
  return (
    <ShellHeaderRegistryProvider>
      <AppShellContent {...props} />
    </ShellHeaderRegistryProvider>
  );
}
